import React, { useState } from 'react';
import { Container, Stack, Title, Card, Group, Text, ActionIcon } from '@mantine/core';
import { IconChevronLeft } from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';

import { PhotoGallery } from '../../components/parent/media/PhotoGallery';
import { PhotoViewer } from '../../components/parent/media/PhotoViewer';
import { SmartLoader } from '../../components/common/LoadingStates';
import { useGroupedPhotos } from '../../hooks/parent/useChildPhotos';
import { useChildPhotosFromDetections, useChildSummaryFromStudent } from '../../hooks/useParent';
import type { ChildPhoto } from '../../services/parentService';

const ChildPhotos: React.FC = () => {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);

  // Use enhanced hooks for better data integration
  const {
    data: child,
    isLoading: childLoading,
    error: childError,
  } = useChildSummaryFromStudent(childId);

  const {
    data: detectionPhotos,
  } = useChildPhotosFromDetections(childId, { limit: 100 });

  const { allPhotos } = useGroupedPhotos(childId);

  // Combine photos from detections with existing photos
  const combinedPhotos = [...(detectionPhotos || []), ...(allPhotos || [])];

  const handlePhotoClick = (photo: ChildPhoto) => {
    const index = combinedPhotos.findIndex(p => p.id === photo.id);
    if (index !== -1) {
      setSelectedPhotoIndex(index);
    }
  };

  const handleCloseViewer = () => {
    setSelectedPhotoIndex(null);
  };

  if (!childId) {
    return (
      <Container size="xl">
        <Card withBorder p="xl">
          <Text ta="center">Invalid child ID</Text>
        </Card>
      </Container>
    );
  }

  return (
    <Container size="xl">
      <Stack gap="lg">
        {/* Header */}
        <SmartLoader
          isLoading={childLoading}
          error={childError as Error | null}
          isEmpty={!child}
        >
          {child && (
            <Card withBorder>
              <Group>
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  onClick={() => navigate('/parent/dashboard')}
                >
                  <IconChevronLeft size={20} />
                </ActionIcon>
                <div>
                  <Title order={2}>{child.first_name}'s Photos</Title>
                  <Text size="sm" c="dimmed">
                    All photos and memories from school
                  </Text>
                </div>
              </Group>
            </Card>
          )}
        </SmartLoader>

        {/* Photo Gallery */}
        <PhotoGallery
          childId={childId}
          onPhotoClick={handlePhotoClick}
        />

        {/* Photo Viewer Modal */}
        {selectedPhotoIndex !== null && (
          <PhotoViewer
            photos={allPhotos}
            initialIndex={selectedPhotoIndex}
            opened={selectedPhotoIndex !== null}
            onClose={handleCloseViewer}
          />
        )}
      </Stack>
    </Container>
  );
};

export default ChildPhotos;
